import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Field } from './utils/api';
import { Button, Input, Textarea, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './components/ui';
import { LoaderIcon, CheckCircleIcon } from './components/Icons';
import { extractDomain } from './utils';
import { getForm, submitResponse } from './utils/api';

type FormWithFields = Form & { fields: Field[] };

const EmbedFormPage: React.FC = () => {
    const { formId } = useParams<{ formId: string }>();
    const [form, setForm] = useState<FormWithFields | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [formData, setFormData] = useState<Record<string, string>>({});
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const fetchForm = useCallback(async () => {
        if (!formId) return;
        setLoading(true);
        try {
            const data = await getForm(formId) as FormWithFields;
            if (data.status !== 'published') {
                throw new Error('This form is not currently accepting responses.');
            }
            data.fields.sort((a, b) => a.order_index - b.order_index);
            setForm(data);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [formId]);

    useEffect(() => {
        fetchForm();
    }, [fetchForm]);

    const validateField = (field: Field, value: string) => {
        // Required field validation
        if (field.required && !value.trim()) {
            return 'This field is required.';
        }
        
        // Skip further validation if field is empty and not required
        if (!value.trim()) {
            return '';
        }

        // Email field specific validation
        if (field.type === 'email') {
            // Basic email format validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                return 'Please enter a valid email address';
            }
        }

        // Number field specific validation
        if (field.type === 'number') {
            // Check if value is a valid number
            if (isNaN(Number(value))) {
                return 'Please enter a valid number';
            }
        }

        // URL field specific validation
        if (field.type === 'url') {
            // First check if it's a valid URL format
            try {
                const url = new URL(value);
                
                // Check disabled domains
                if (field.disabled_domains && field.disabled_domains.length > 0) {
                    const domain = extractDomain(value);
                    if (domain && field.disabled_domains.includes(domain)) {
                        return `Submissions from ${domain} are not allowed.`;
                    }
                }
            } catch (e) {
                // If URL constructor fails, it's not a valid URL
                return 'Please enter a valid URL (e.g., https://example.com)';
            }
        }

        // Custom regex validation (applies to all field types)
        if (value && field.validation_regex) {
            try {
                const regex = new RegExp(field.validation_regex);
                if (!regex.test(value)) {
                    return field.validation_message || 'Invalid format.';
                }
            } catch (e) {
                console.error("Invalid regex:", field.validation_regex);
                return 'Invalid format.';
            }
        }

        return '';
    };

    const handleInputChange = (fieldId: string, value: string) => {
        const field = form?.fields.find(f => f.id === fieldId);
        if (!field) return;

        setFormData(prev => ({ ...prev, [fieldId]: value }));
        const error = validateField(field, value);
        setValidationErrors(prev => ({ ...prev, [fieldId]: error }));
    };

    const handleInputBlur = (fieldId: string, value: string) => {
        const field = form?.fields.find(f => f.id === fieldId);
        if (!field) return;

        // Force validation on blur
        const error = validateField(field, value);
        setValidationErrors(prev => ({ ...prev, [fieldId]: error }));
    };

    const handleKeyDown = (e: React.KeyboardEvent, fieldId: string) => {
        // Prevent Tab/Enter navigation if current field has validation errors
        if ((e.key === 'Tab' || e.key === 'Enter') && validationErrors[fieldId]) {
            e.preventDefault();
            // Focus stays on current field
            const element = document.getElementById(fieldId);
            if (element) {
                element.focus();
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form || !formId) return;

        let allValid = true;
        const newErrors: Record<string, string> = {};
        for (const field of form.fields) {
            const error = validateField(field, formData[field.id] || '');
            if (error) {
                newErrors[field.id] = error;
                allValid = false;
            }
        }
        setValidationErrors(newErrors);

        if (allValid) {
            setIsSubmitting(true);
            try {
                await submitResponse(formId, formData);
                setIsSubmitted(true);
            } catch (err) {
                setError('An error occurred during submission. Please try again.');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const isFormValid = form ? form.fields.every(field => !validateField(field, formData[field.id] || '')) : false;

    if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><LoaderIcon className="h-8 w-8" /></div>;
    if (error) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><p className="text-red-600 text-lg">{error}</p></div>;
    if (!form) return null;
    
    if (isSubmitted) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-lg text-center">
                    <CardContent className="p-8">
                        <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                        <CardTitle className="text-2xl mb-2">Thank You!</CardTitle>
                        <CardDescription>Your response has been submitted successfully.</CardDescription>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-lg">
                <CardHeader>
                    <CardTitle className="text-2xl">{form.name}</CardTitle>
                    {form.description && <CardDescription>{form.description}</CardDescription>}
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent className="space-y-6">
                        {form.fields.map(field => (
                            <div key={field.id}>
                                <label htmlFor={field.id} className="block text-sm font-medium mb-1">
                                    {field.label} {field.required && <span className="text-red-500">*</span>}
                                </label>
                                {field.type === 'textarea' ? (
                                    <Textarea
                                        id={field.id}
                                        value={formData[field.id] || ''}
                                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                                        onBlur={(e) => handleInputBlur(field.id, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(e, field.id)}
                                        placeholder={field.placeholder || ''}
                                        className={validationErrors[field.id] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                                        required={field.required}
                                    />
                                ) : field.type === 'select' ? (
                                    <select
                                        id={field.id}
                                        value={formData[field.id] || ''}
                                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                                        onBlur={(e) => handleInputBlur(field.id, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(e, field.id)}
                                        className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${validationErrors[field.id] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-input'}`}
                                        required={field.required}
                                    >
                                        <option value="">{field.placeholder || 'Select an option'}</option>
                                        {field.field_options?.map(option => <option key={option} value={option}>{option}</option>)}
                                    </select>
                                ) : (
                                    <Input
                                        id={field.id}
                                        type={field.type}
                                        value={formData[field.id] || ''}
                                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                                        onBlur={(e) => handleInputBlur(field.id, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(e, field.id)}
                                        placeholder={field.placeholder || ''}
                                        pattern={field.validation_regex || undefined}
                                        title={field.validation_message || undefined}
                                        className={validationErrors[field.id] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                                        required={field.required}
                                    />
                                )}
                                {validationErrors[field.id] && (
                                    <div className="flex items-center space-x-2 mt-2">
                                        <svg className="h-4 w-4 text-red-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <p className="text-red-600 text-sm font-medium">{validationErrors[field.id]}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </CardContent>
                    <CardFooter>
                        <Button type="submit" className="w-full" disabled={!isFormValid || isSubmitting}>
                            {isSubmitting ? <LoaderIcon className="h-4 w-4 mr-2" /> : null}
                            Submit
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
};

export default EmbedFormPage;